import { useEffect, useState } from "react";
import { Modal } from "../Modal";
import { useDispatch } from "react-redux";
import { InventoryModel } from "../../../models/inventory.model";
import { inventoryService } from "../../../service/inventory/inventory.service";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Icon,
  IconButton,
  useToast,
  Spinner,
  Text,
  Tooltip,
  useClipboard,
  Badge,
} from "@chakra-ui/react";
import { CheckCircle, Copy, Laptop } from "lucide-react"; // or any icon set
import { ButtonCustom } from "../../form/controls/form.button";
import { errorToast, successToast } from "../../../constants/toast.constants";

const PAGE = 1;
const PAGE_SIZE = 1000;

export const RequestQuoteModal = ({
  isOpen,
  onClose,
  onSubmit,
  inventoryIds,
  totalCount
}) => {
  const toast = useToast();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([] as InventoryModel[]);
  const [submitted, setSubmitted] = useState(false);

  const total = inventory.length;

  const handleSubmit = async (e?: any) => {
    e?.preventDefault();
    setLoading(true);

    try {
      // Artificially wait 1.5 seconds
      await new Promise((resolve) => setTimeout(resolve, 1500));
      
      // Dont await this
      inventoryService.requestQuote(inventoryIds);
      setSubmitted(true);
    } catch (error) {
      setLoading(false);
      toast(
        errorToast(
          "We have an issue sending this Quote. Please try again later."
        )
      );
    }
  };

  const handleClose = () => {
    setLoading(false);
    setSubmitted(false);
    onClose();
  };

  const init = async () => {
    setLoading(true);
    await inventoryService
      .getList({
        ids: inventoryIds,
      }, undefined, PAGE, PAGE_SIZE)
      .then((resp) => {
        setInventory(resp.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isOpen) {
      init();
    }

    return () => {};
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width="800px"
      header={
        <Flex align="center" gap={2}>
          <Icon as={Laptop} />
          Request a Quote
          {total > 0 && (
            <Badge ml="2" colorScheme="blue" variant="subtle">
              {total} / {totalCount}
            </Badge>
          )}
        </Flex>
      }
    >
      {submitted ? (
        <>
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={12}
            gap={3}
          >
            <CheckCircle width={48} height={48} color="green" />
            <Text fontSize="xl" fontWeight="semibold">
              Rebate Quote Request Submitted
            </Text>
            <Box  fontSize="sm" color="gray.600">
              The Zolo team is reviewing your devices. <br/>You'll receive your rebate quote via email shortly
            </Box>
          </Flex>

          <Flex justify="flex-end" gap={2} mt={4}>
            <ButtonCustom neutral onClick={handleClose}>Close</ButtonCustom>
          </Flex>
        </>
      ) : (
        <>
          <Text mb="3" color="gray.600">
            Request a quote for the selected devices.
          </Text>

          {/* Loading state */}
          {loading && (
            <Flex align="center" justify="center" py={12} gap={3}>
              <Spinner />
              <Text>Preparing Quote Request...</Text>
            </Flex>
          )}

          {/* Empty state */}
          {!loading && total === 0 && (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py={12}
              border="1px dashed"
              borderColor="gray.200"
              rounded="xl"
              className="bg-gray-50"
            >
              <Text fontWeight="semibold" mb={1}>
                No devices selected
              </Text>
              <Text fontSize="sm" color="gray.600">
                Choose one or more devices to request a quote.
              </Text>
            </Flex>
          )}

          {/* List */}
          {!loading && total > 0 && (
            <Box
              border="1px solid"
              borderColor="gray.100"
              rounded="xl"
              overflow="hidden"
              className="shadow-sm"
            >
              {/* Header */}
              <Grid
                templateColumns="3fr 2fr 1fr 2fr 84px"
                gap={3}
                px={4}
                py={2}
                bg="gray.50"
                borderBottom="1px solid"
                borderColor="gray.100"
                fontSize="sm"
                color="gray.600"
              >
                <GridItem>Model</GridItem>
                <GridItem>Processor</GridItem>
                <GridItem>RAM</GridItem>
                <GridItem>Storage</GridItem>
                <GridItem>Serial</GridItem>
              </Grid>

              <Box maxH="320px" overflowY="auto">
                {inventory.map((item, idx) => (
                  <Row key={item.id ?? item.serialNumber ?? idx} item={item} />
                ))}
              </Box>
            </Box>
          )}

          <Flex justify="flex-end" gap={2} mt={4}>
            <ButtonCustom
              secondary
              neutral
              onClick={onClose}
              isDisabled={loading}
            >
              Cancel
            </ButtonCustom>
            <ButtonCustom
              onClick={handleSubmit}
              isLoading={loading}
              loadingText="Submitting"
            >
              Request Rebate Quote
            </ButtonCustom>
          </Flex>
        </>
      )}
    </Modal>
  );
};

function Row({ item }: { item: InventoryModel }) {
  const { hasCopied, onCopy } = useClipboard(item.serialNumber ?? "");
  const ramText =
    item.ram === undefined || item.ram === null
      ? "—"
      : typeof item.ram === "number"
      ? `${item.ram} GB`
      : `${item.ram} GB`;

  const storageText =
    (item.storageSize ?? "—") + " " + (item.storageType ?? "—");

  return (
    <Grid
      templateColumns="3fr 2fr 1fr 2fr 84px"
      gap={3}
      px={4}
      py={3}
      alignItems="center"
      fontSize="14px"
      _notLast={{ borderBottom: "1px solid", borderColor: "gray.100" }}
      className="hover:bg-gray-50 transition-colors"
    >
      <GridItem>
        <Tooltip label={item.model} hasArrow placement="top" openDelay={300}>
          <Text noOfLines={1}>{item.model || "—"}</Text>
        </Tooltip>
      </GridItem>

      <GridItem>
        <Text>{item.processor || "—"}</Text>
      </GridItem>

      <GridItem>
        <Text>{ramText}</Text>
      </GridItem>

      <GridItem>
        <Text>{storageText}</Text>
      </GridItem>

      <GridItem>
        <Flex align="center" gap={2} justify="flex-end">
          <Tooltip
            label={hasCopied ? "Copied!" : "Copy serial"}
            closeOnClick={false}
          >
            <IconButton
              aria-label="Copy serial number"
              icon={<Icon as={Copy} />}
              size="sm"
              variant="ghost"
              onClick={onCopy}
            />
          </Tooltip>
          <Text
            fontFamily="mono"
            noOfLines={1}
            maxW="80px"
            textAlign="right"
            fontSize="14px"
          >
            {item.serialNumber || "—"}
          </Text>
        </Flex>
      </GridItem>
    </Grid>
  );
}
